import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CORE_CONFIG_TOKEN } from '@ndk/ng-core/component/core.config';
import { LocalStorageService } from '@ndk/ng-storage';
import { Observable } from 'rxjs';
import { take, tap } from 'rxjs/operators';

import { ICoreConfigInternal } from '@core/core.config.model';
import { LocalStorageKeys } from '@core/storage/local-storage.enum';
import { ICostOptions } from '@features/settings/general/cost-options/shared/settings-general-cost-options.model';
import { SettingsGeneralCostOptionsService } from '@features/settings/general/cost-options/shared/settings-general-cost-options.service';

import { AuthTokenService } from './auth-token.service';
import { IAuthLastLoginData } from './shared/auth-login-data.model';
import { IAuth } from './shared/auth.model';
import { IRegisterPasswordCredentials } from './shared/register-password-credentials.model';
import { IRegisterPassword } from './shared/register-password.model';
import { IUserRecoverPasswordCommand, IUserRecoverStrongPasswordCommand } from './shared/request-password.model';

@Injectable()
export class AuthService {
    private apiUrl: string;
    private httpHeaders: HttpHeaders = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });

    private status: boolean = false;

    public get loggedIn(): boolean {
        return this.status;
    }

    public set loggedIn(status: boolean) {
        this.status = status;
    }

    constructor(
        @Inject(CORE_CONFIG_TOKEN) config: ICoreConfigInternal,
        private http: HttpClient,
        private localStorageService: LocalStorageService,
        private authTokenService: AuthTokenService,
        private router: Router,
        private costOptionsService: SettingsGeneralCostOptionsService,
    ) {
        this.apiUrl = config.apiEndpoint;
    }

    public requestPassword(data: IUserRecoverPasswordCommand): Observable<boolean> {
        const content: any = {
            domainName: data.domain.trim(),
            logonName: data.username.trim(),
            enterpriseName: data.enterprise.trim(),
            email: data.email.trim(),
        };

        return this.http.post<boolean>(`${ this.apiUrl }api/public/request-password`, content);
    }

    public requestStrongPassword(data: IUserRecoverStrongPasswordCommand): Observable<string> {
        const content: any = {
            domainName: data.domain.trim(),
            logonName: data.username.trim(),
            enterpriseName: data.enterprise.trim(),
            email: data.email.trim(),
            currentPassword : data.currentPassword,
        };

        return this.http.post<string>(`${ this.apiUrl }api/public/request-strong-password`, content);
    }

    public registerPasswordValidate(code: string): Observable<IRegisterPasswordCredentials> {
        return this.http.post<IRegisterPasswordCredentials>(`${ this.apiUrl }api/public/reset-password-data`, { code });
    }

    public registerPassword(data: IRegisterPassword): Observable<boolean> {
        const content: IRegisterPassword = {
            code: data.code.trim(),
            newPassword: data.newPassword.trim(),
        };

        return this.http.post<boolean>(`${ this.apiUrl }api/public/reset-password`, content);
    }

    public login(data: IAuth, isDealer: boolean): Observable<void> {
        // tslint:disable-next-line:prefer-template
        const content: string = 'grant_type=password'
            + (isDealer ? `&partner=${ data.dealer.trim() }` : '')
            + `&enterprise=${ data.company.trim() }`
            + `&username=${ encodeURIComponent(data.username.trim()) }`
            + `&password=${ encodeURIComponent(data.password) }`;

        return this.http
            .post(`${ this.apiUrl }token`, content, { headers: this.httpHeaders })
            .pipe(
                tap((response: any) => {
                    this.handleAuthToken(true, <string>response.access_token);
                    this.createLastLogin(data, isDealer);
                }),
            );
    }

    public loginFromMps(data: string): Observable<void> {
        const content: string = 'grant_type=password' + `&mps-passport=${ data }`;

        return this.http
            .post(`${ this.apiUrl }token`, content, { headers: this.httpHeaders })
            .pipe(tap((response: any) => this.handleAuthToken(true, <string>response.access_token)));
    }

    public logout(): void {
        this.localStorageService.deleteValue(LocalStorageKeys.PoliciesQuotasManagement);

        this.handleAuthToken(false);
        this.navigateToLogin();
    }

    public isAlive(): Observable<boolean> {
        return this.http
            .get<boolean>(`${ this.apiUrl }api/public/is-alive`)
            .pipe(tap((response: boolean) => this.loggedIn = response));
    }

    public checkPeriodDays(): Observable<boolean> {
        return this.http.get<boolean>(`${ this.apiUrl }api/accounting-license/check-period-days`);
    }

    public createLastLogin(data: IAuth, isDealer: boolean): void {
        let lastLogin: IAuthLastLoginData = this.localStorageService.getValue(LocalStorageKeys.LastLogin);

        if (!lastLogin) {
            lastLogin = <IAuthLastLoginData>{};
        }

        lastLogin.isDealer = isDealer;

        if (isDealer) {
            lastLogin.dealer = {
                dealer: data.dealer.trim(),
                company: data.company.trim(),
                username: data.username.trim(),
            };
            lastLogin.company = lastLogin.company
                || {
                company: '',
                username: '',
            };
        } else {
            lastLogin.company = {
                company: data.company.trim(),
                username: data.username.trim(),
            };
            lastLogin.dealer = lastLogin.dealer
                || {
                dealer: '',
                company: '',
                username: '',
            };
        }

        this.localStorageService.setValue(LocalStorageKeys.LastLogin, lastLogin);
    }

    public navigateToLogin(params?: any): void {
        const lastLogin: IAuthLastLoginData = this.localStorageService.getValue(LocalStorageKeys.LastLogin);
        let loginUrl: string = '/login/company';

        if (lastLogin && lastLogin.isDealer) {
            loginUrl = '/login/dealer';
        }
        if (params) {
            this.router.navigate([loginUrl], params);
        } else {
            this.router.navigate([loginUrl]);
        }
    }

    private handleAuthToken(loggedIn: boolean, token?: string): void {
        this.loggedIn = loggedIn;

        if (loggedIn) {
            this.authTokenService.createToken(token);
            this.localStorageService.setValue(LocalStorageKeys.AccountType, +this.authTokenService.token.acct);

            this.costOptionsService.getCostOptions()
                .pipe(take(1))
                .subscribe({
                    next: (options: ICostOptions): void => {
                        this.localStorageService.setValue(LocalStorageKeys.DecimalPlaces, options.decimalPlaces);
                    },
                });
        } else {
            this.authTokenService.resetToken();
            this.localStorageService.deleteValue(LocalStorageKeys.AccountType);
            this.localStorageService.deleteValue(LocalStorageKeys.DecimalPlaces);
        }
    }
}
